/** HUD element rendering the player's HP bar, stamina bar, and dash charge pips. */
import Phaser from 'phaser';
import { TAU } from '../utils/Math';
import { crispGraphics, crispRoundedRect, ensureCircleTexture, CRISP_SCALE } from './CrispUtil';
import { Color } from './Theme';

const S = CRISP_SCALE;
const INV_S = 1 / S;

const HUD_X = 12;
const HUD_Y = 12;
const HUD_PAD = 6;

// Health bar
const HP_BAR_WIDTH = 120;
const HP_BAR_HEIGHT = 10;

// Stamina bar — flush against HP bar bottom
const STAM_BAR_WIDTH = 120;
const STAM_BAR_HEIGHT = 6;
const STAM_Y_OFFSET = HP_BAR_HEIGHT + 1;

// Dash pips — positioned to the right of bars
const PIP_DISK_RADIUS = 5;
const PIP_RING_WIDTH = 3;
const PIP_RING_RADIUS = PIP_DISK_RADIUS + PIP_RING_WIDTH / 2;
const PIP_SPACING = 18;
const PIP_GAP = 10;  // gap between bar right edge and first pip
const PIP_BASE_X = HUD_X + HP_BAR_WIDTH + PIP_GAP + PIP_RING_RADIUS;
// Center pips vertically with bar block (HP + stamina)
const BAR_BLOCK_HEIGHT = HP_BAR_HEIGHT + 1 + STAM_BAR_HEIGHT;
const PIP_CENTER_Y = HUD_Y + BAR_BLOCK_HEIGHT / 2;

// Max dash pips we pre-allocate Image objects for
const MAX_PIPS = 4;

// Shared underlay covers combat HUD + attunement icons below (icons at y=44, radius 11)
const UNDERLAY_BOTTOM = 57;

/** Compute the underlay right edge based on the actual number of dash pips. */
function underlayRight(pipCount: number): number {
  if (pipCount <= 0) return HUD_X + HP_BAR_WIDTH + HUD_PAD;
  return PIP_BASE_X + (pipCount - 1) * PIP_SPACING + PIP_RING_RADIUS + HUD_PAD;
}

// Canvas size for the refill arc texture (in canvas pixels, not logical)
const ARC_CANVAS_SIZE = Math.ceil((PIP_RING_RADIUS + PIP_RING_WIDTH) * 2 + 4) * S;

/** Convert a 0xRRGGBB number to a CSS hex string. */
function hexStr(color: number): string {
  return '#' + color.toString(16).padStart(6, '0');
}

// Refill color gradient: red (0) → yellow (0.5) → green (1)
function refillColor(t: number): number {
  if (t <= 0.5) {
    // Red → Yellow
    const p = t * 2;
    const r = 0xcc;
    const g = Math.round(0xcc * p);
    return (r << 16) | (g << 8);
  }
  // Yellow → Green
  const p = (t - 0.5) * 2;
  const r = Math.round(0xcc * (1 - p));
  const g = 0xcc;
  return (r << 16) | (g << 8);
}


/** Renders the combat HUD: health bar, stamina bar, and dash pip indicators. */
export class CombatHUD {
  private scene: Phaser.Scene;
  private graphics: Phaser.GameObjects.Graphics;

  // Health
  private hpRatio: number = 1;

  // Stamina
  private stamRatio: number = 1;
  private stamEmpty: boolean = false;

  // Dash
  private dashCharges: number = 1;
  private dashMax: number = 1;
  private dashRefill: number = 0;

  // Dirty flag — skip draw() when nothing changed
  private dirty: boolean = true;

  // Canvas-texture pip disk Images (smooth circles instead of WebGL geometry)
  private pipDisks: Phaser.GameObjects.Image[] = [];
  private diskTexKey: string;

  // Dynamic underlay — rebuilt when dashMax changes
  private underlay: Phaser.GameObjects.Image;
  private underlayPipCount: number = 1;

  // Canvas-texture refill arc (smooth partial ring)
  private arcCanvas: HTMLCanvasElement;
  private arcCtx: CanvasRenderingContext2D;
  private arcImage: Phaser.GameObjects.Image;
  private arcTexKey: string;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.graphics = crispGraphics(scene);
    this.graphics.setDepth(100);

    // Dynamic underlay — sized to actual dashMax, rebuilt when it changes
    this.underlay = this.buildUnderlay(this.dashMax);

    // Pre-create pip disk texture (canvas-rendered for anti-aliasing)
    this.diskTexKey = ensureCircleTexture(scene, PIP_DISK_RADIUS * S);

    // Pre-create pip disk Images
    for (let i = 0; i < MAX_PIPS; i++) {
      const img = scene.add.image(0, 0, this.diskTexKey)
        .setScale(INV_S).setDepth(100).setVisible(false).setAlpha(0.9);
      this.pipDisks.push(img);
    }

    // Create reusable canvas for the refill arc
    this.arcCanvas = document.createElement('canvas');
    this.arcCanvas.width = ARC_CANVAS_SIZE;
    this.arcCanvas.height = ARC_CANVAS_SIZE;
    this.arcCtx = this.arcCanvas.getContext('2d')!;
    this.arcTexKey = '_dash-refill-arc';
    scene.textures.addCanvas(this.arcTexKey, this.arcCanvas);
    scene.textures.get(this.arcTexKey).setFilter(Phaser.Textures.FilterMode.LINEAR);
    this.arcImage = scene.add.image(0, 0, this.arcTexKey)
      .setScale(INV_S).setDepth(100).setVisible(false).setAlpha(0.9);
  }

  /** Create the underlay Image sized for the given pip count. */
  private buildUnderlay(pipCount: number): Phaser.GameObjects.Image {
    const right = underlayRight(pipCount);
    const ulW = right - HUD_X + HUD_PAD;
    const ulH = UNDERLAY_BOTTOM - HUD_Y + HUD_PAD * 2;
    this.underlayPipCount = pipCount;
    return crispRoundedRect(this.scene,
      (HUD_X - HUD_PAD) + ulW / 2,
      (HUD_Y - HUD_PAD) + ulH / 2,
      ulW, ulH, 4, Color.black, 0.35,
    ).setDepth(99).setVisible(false);
  }

  /** Update the HP bar with current and maximum health values. */
  setHealth(current: number, max: number): void {
    const ratio = Math.max(0, current / max);
    if (ratio !== this.hpRatio) {
      this.hpRatio = ratio;
      this.dirty = true;
    }
  }

  /** Update the stamina bar with current and maximum stamina values. */
  setStamina(current: number, max: number): void {
    const ratio = Math.max(0, current / max);
    const empty = current <= 0;
    if (ratio !== this.stamRatio || empty !== this.stamEmpty) {
      this.stamRatio = ratio;
      this.stamEmpty = empty;
      this.dirty = true;
    }
  }

  /** Update dash pip state with current charge count, maximum charges, and refill progress (0–1). */
  setDashCharges(charges: number, max: number, refillProgress: number): void {
    if (charges !== this.dashCharges || max !== this.dashMax || refillProgress !== this.dashRefill) {
      // Rebuild underlay if pip count changed
      if (max !== this.dashMax) {
        this.underlay.destroy();
        this.underlay = this.buildUnderlay(max);
      }
      this.dashCharges = charges;
      this.dashMax = max;
      this.dashRefill = refillProgress;
      this.dirty = true;
    }
  }

  /** Redraw all HUD elements using the latest stored values. */
  draw(): void {
    if (!this.dirty) return;
    this.dirty = false;
    this.graphics.clear();

    // --- Health bar ---
    const hpX = HUD_X * S;
    const hpY = HUD_Y * S;

    // Background
    this.graphics.fillStyle(Color.panelBg, 0.8);
    this.graphics.fillRect(hpX, hpY, HP_BAR_WIDTH * S, HP_BAR_HEIGHT * S);

    // Fill
    this.graphics.fillStyle(Color.hpFill, 0.9);
    this.graphics.fillRect(hpX, hpY, HP_BAR_WIDTH * this.hpRatio * S, HP_BAR_HEIGHT * S);

    // --- Stamina bar ---
    const stamX = HUD_X * S;
    const stamY = (HUD_Y + STAM_Y_OFFSET) * S;

    // Background
    this.graphics.fillStyle(Color.panelBg, 0.8);
    this.graphics.fillRect(stamX, stamY, STAM_BAR_WIDTH * S, STAM_BAR_HEIGHT * S);

    // Fill
    const stamColor = this.stamEmpty ? Color.staminaEmpty : Color.staminaFill;
    this.graphics.fillStyle(stamColor, 0.9);
    this.graphics.fillRect(stamX, stamY, STAM_BAR_WIDTH * this.stamRatio * S, STAM_BAR_HEIGHT * S);

    // --- Dash pips (canvas-texture Images for smooth circles, right of bars) ---
    const pipY = PIP_CENTER_Y;
    let showArc = false;

    for (let i = 0; i < MAX_PIPS; i++) {
      const img = this.pipDisks[i];
      if (i >= this.dashMax) {
        img.setVisible(false);
        continue;
      }

      const pipX = PIP_BASE_X + i * PIP_SPACING;
      const ready = i < this.dashCharges;

      img.setPosition(pipX, pipY);
      img.setTint(ready ? Color.dashReady : Color.dashUnavail);
      img.setVisible(true);

      // Refill arc for the next charging pip
      if (!ready && i === this.dashCharges && this.dashRefill > 0) {
        showArc = true;
        this.drawArc(pipX, pipY);
      }
    }

    if (!showArc) {
      this.arcImage.setVisible(false);
    }
  }

  /** Redraw the refill arc on the reusable canvas and position its Image. */
  private drawArc(pipX: number, pipY: number): void {
    const ctx = this.arcCtx;
    const center = ARC_CANVAS_SIZE / 2;

    ctx.clearRect(0, 0, ARC_CANVAS_SIZE, ARC_CANVAS_SIZE);

    const startAngle = -Math.PI / 2;
    const endAngle = startAngle + TAU * this.dashRefill;
    const color = refillColor(this.dashRefill);

    ctx.strokeStyle = hexStr(color);
    ctx.lineWidth = PIP_RING_WIDTH * S;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.arc(center, center, PIP_RING_RADIUS * S, startAngle, endAngle, false);
    ctx.stroke();

    // Refresh the Phaser CanvasTexture so the Image shows the updated arc
    (this.scene.textures.get(this.arcTexKey) as Phaser.Textures.CanvasTexture).refresh();

    this.arcImage.setPosition(pipX, pipY);
    this.arcImage.setVisible(true);
  }

  /** Destroy the graphics object and free resources. */
  destroy(): void {
    this.underlay.destroy();
    this.graphics.destroy();
    for (const img of this.pipDisks) img.destroy();
    this.arcImage.destroy();
    if (this.scene.textures.exists(this.arcTexKey)) {
      this.scene.textures.remove(this.arcTexKey);
    }
  }
}
